<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import { STORIES } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import { DATE_FORMAT_JA_WITH_DAY_OF_WEEK } from '@/constants/dateFormat'
import StoryFollowCTA from '@/components/follow/StoryFollowCTA.vue'
import PublisherName from '@/components/contents/PublisherName.vue'
import DateDefault from '@/components/date/DateDefault.vue'
import SectionHeadingForContentDetail from '@/components/heading/SectionHeadingForContentDetail.vue'
import LayoutBackNumberReportCards from '@/components/contents/LayoutBackNumberReportCards.vue'
import LayoutContentsCardsRows from '@/components/contents/LayoutContentsCardsRows.vue'
import ProgressIndicator from '@/components/loading/ProgressIndicator.vue'
import LogoImage from '@/images/logo-bizhint_02.svg'
import stateInitializer from '@/models/stateInitializer'
import { useClientStore } from '@/stores/client'

import type { Ref } from 'vue'

export default defineComponent({
  name: 'StoryMain',

  components: {
    StoryFollowCTA,
    PublisherName,
    DateDefault,
    SectionHeadingForContentDetail,
    LayoutBackNumberReportCards,
    LayoutContentsCardsRows,
    ProgressIndicator,
  },

  setup() {
    // constants
    const DEFAULT_PAGE = 1 as const
    const DEFAULT_PER = 30 as const

    // store
    const clientStore = useClientStore()

    // 特集コンテンツの現在のページ番号
    const page: Ref<{
      report: number | null,
      article: number | null,
    }> = ref({
      report: null,
      article: null,
    })

    const isFetching = ref({
      report: false,
      article: false,
    })

    interface State {
      story: StoryItem,
    }
    const state: State = reactive({
      story: {
        id: 0,
        title: '',
        thumbImageUrl: '',
        description: '',
        publisher: {
          iconUrl: '',
          name: '',
        },
        updatedAt: '',
        themeId: 0,
        isFollowed: false,
        report: {
          items: [],
          total: 0,
        },
        article: {
          items: [],
          total: 0,
        },
      },
    })
    stateInitializer(state, 'main')

    const reportItems = computed<ReportItem[]>(() => {
      return state.story.report && state.story.report.items
        ? state.story.report.items
        : []
    })

    const articleItems = computed<ArticleItem[]>(() => {
      return state.story.article && state.story.article.items
        ? state.story.article.items
        : []
    })

    onMounted(() => {
      fetchReports()
      fetchArticles()
    })

    /**
     * 特集に紐づくレポートのバックナンバーを取得
     */
    const fetchReports = async (per?: number) => {
      if (isFetching.value.report) return

      const pageNumber = page.value.report === null
        ? DEFAULT_PAGE
        : page.value.report + 1

      const path = STORIES.FETCH_REPORTS.path({
        id: state.story.id.toString(),
      })

      const endpoint = { ...STORIES.FETCH_REPORTS, path }

      const params = {
        page: pageNumber,
        per: per,
      }

      isFetching.value.report = true

      try {
        await apiRequestManager.handleRequest({
          endpoint,
          params,
          callback: (response: Response<StoryReportsResponse>, isSuccess: boolean) => {
            if (isSuccess) {
              response.data.reports.forEach(report => {
                state.story.report?.items?.push({
                  id: report.id,
                  title: report.title,
                  thumbImageUrl: report.thumbImageUrl,
                  to: report.to,
                  vol: report.vol,
                  publishDate: report.date,
                  comment: {
                    total: report.commentCount || 0,
                  },
                  isRead: report.readByMe || false,
                })
              })

              state.story.report!.total = response.data.pagination.totalCount
              page.value.report = pageNumber
            }
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        isFetching.value.report = false
      }
    }

    /**
     * 特集に紐づく関連ニュースを取得
     */
    const fetchArticles = async (per?: number) => {
      if (isFetching.value.article) return

      const pageNumber = page.value.article === null
        ? DEFAULT_PAGE
        : page.value.article + 1

      const path = STORIES.FETCH_ARTICLES.path({
        id: state.story.id.toString(),
      })

      const endpoint = { ...STORIES.FETCH_ARTICLES, path }

      const params = {
        page: pageNumber,
        per: per,
      }

      isFetching.value.article = true

      try {
        await apiRequestManager.handleRequest({
          endpoint,
          params,
          callback: (response: Response<StoryArticlesResponse>, isSuccess: boolean) => {
            if (isSuccess) {
              response.data.articles.forEach(article => {
                state.story.article?.items?.push({
                  id: article.id,
                  title: article.title,
                  thumbImageUrl: article.thumbImageUrl,
                  to: article.to,
                  publisher: {
                    name: article.publisher.name,
                    iconUrl: article.publisher.iconUrl,
                  },
                  comment: {
                    total: article.commentCount || 0,
                  },
                  isRead: article.readByMe || false,
                })
              })

              state.story.article!.total = response.data.pagination.totalCount
              page.value.article = pageNumber
            }
          }
        })
      } catch (error) {
        console.log(error)
      } finally {
        isFetching.value.article = false
      }
    }

    return {
      // images
      LogoImage,

      // constants
      DATE_FORMAT_JA_WITH_DAY_OF_WEEK,
      DEFAULT_PER,

      // store
      clientStore,

      // data
      state,
      isFetching,

      // computed
      reportItems,
      articleItems,

      // methods
      fetchReports,
      fetchArticles,
    }
  },
})
</script>

<template>
  <section class="section">
    <div class="l-unit__story">
      <!-- 見出し -->
      <div class="l-unit__story--header">
        <div class="o-heading__page">
          <h1 class="o-heading__page--item">
            {{ state.story.title }}
          </h1>
        <!-- /.o-heading__page --></div>
      <!-- /.l-unit__story header --></div>

      <!-- メインビジュアル -->
      <div
        v-if="state.story.thumbImageUrl"
        class="l-unit__story--image is-hero">
        <img
          class="img"
          :src="state.story.thumbImageUrl"
          fetchpriority="high"
          alt="">
      <!-- /.l-unit__story image --></div>

      <!-- 特殊説明 -->
      <div
        v-if="state.story.description"
        class="l-unit__story--description is-introduction">
        <p class="p u-text__small">
          {{ state.story.description }}
        </p>
      <!-- /.l-unit__story description --></div>

      <!-- バックナンバー -->
      <div
        v-if="reportItems.length"
        class="l-unit__story--description is-report">
        <div
          class="l-unit__storyItem"
          :data-fetching="isFetching.report">
          <div class="l-unit__storyItem--header">
            <SectionHeadingForContentDetail>
              <template #primary>
                バックナンバー
              </template>
              <template
                v-if="state.story.report?.total"
                #secondary>
                ({{ state.story.report.total }})
              </template>
            </SectionHeadingForContentDetail>
          <!-- /.l-unit__storyItem header --></div>

          <div class="l-unit__storyItem--description">
            <LayoutBackNumberReportCards :items="reportItems" />
          <!-- /.l-unit__storyItem description --></div>

          <div
            v-if="(state.story.report?.total ?? 0) > reportItems.length"
            class="l-unit__storyItem--action">
            <button
              class="button u-action__down"
              :disabled="isFetching.report"
              @click="fetchReports(DEFAULT_PER)">
              バックナンバーをもっと見る
            </button>
          <!-- /.l-unit__storyItem action --></div>

          <div class="l-unit__storyItem--footer">
            <ProgressIndicator size="small" />
          <!-- /.l-unit__storyItem footer --></div>
        <!-- /.l-unit__storyItem --></div>
      <!-- /.l-unit__story description --></div>

      <!-- 関連ニュース -->
      <div
        v-if="articleItems.length"
        class="l-unit__story--description is-article">
        <div
          class="l-unit__storyItem"
          :data-fetching="isFetching.article">
          <div class="l-unit__storyItem--header">
            <SectionHeadingForContentDetail>
              <template #primary>
                関連ニュース
              </template>
              <template
                v-if="state.story.article?.total"
                #secondary>
                ({{ state.story.article.total }})
              </template>
            </SectionHeadingForContentDetail>
          <!-- /.l-unit__storyItem header --></div>

          <div class="l-unit__storyItem--description">
            <LayoutContentsCardsRows
              :items="articleItems"
              :heading-options="{
                maxLine: 2,
              }"
              :image-options="{
                width: {
                  pc: 72,
                  sp: 72,
                },
                height: {
                  pc: 72,
                  sp: 72,
                },
              }" />
          <!-- /.l-unit__storyItem description --></div>

          <div
            v-if="(state.story.article?.total ?? 0) > articleItems.length"
            class="l-unit__storyItem--action">
            <button
              class="button u-action__down"
              :disabled="isFetching.article"
              @click="fetchArticles(DEFAULT_PER)">
              関連ニュースをもっと見る
            </button>
          <!-- /.l-unit__storyItem action --></div>

          <div class="l-unit__storyItem--footer">
            <ProgressIndicator size="small" />
          <!-- /.l-unit__storyItem footer --></div>
        <!-- /.l-unit__storyItem --></div>
      <!-- /.l-unit__story description --></div>

      <!-- フォローボタン -->
      <div
        v-if="!clientStore.context.isApp && state.story.isFollowed !== undefined"
        class="l-unit__story--action is-follow">
        <StoryFollowCTA
          :story-id="state.story.id"
          :is-active="state.story.isFollowed"
          @update:follow="state.story.isFollowed = $event" />
      <!-- /.l-unit__story action --></div>

      <!-- 公開者 -->
      <div
        v-if="state.story.publisher"
        class="l-unit__story--footer is-publisher">
        <PublisherName
          :image="state.story.publisher.iconUrl || LogoImage"
          :label="state.story.publisher.name || 'BizHint 編集部'" />
      <!-- /.l-unit__story footer --></div>

      <!-- 掲載日 -->
      <div class="l-unit__story--footer is-date">
        <DateDefault
          :date="state.story.updatedAt"
          :format="DATE_FORMAT_JA_WITH_DAY_OF_WEEK"
          suffix="更新" />
      <!-- /.l-unit__story footer --></div>
    <!-- /.l-unit__story --></div>
  </section>
</template>

<style lang="sass" scoped>
// layout
.l-unit__story
  $root: &
  $date-gap: 1em
  display: grid
  grid-template-columns: auto $date-gap 1fr

  & &--header
    grid-column: 1 / 4
    grid-row: 2 / 3

  & &--image
    grid-column: 1 / 4
    grid-row: 1 / 2
    margin-bottom: 1em

  & &--description
    &.is-introduction
      grid-column: 1 / 4
      grid-row: 5 / 6
      line-height: 1.8
      margin-top: changeSizePx2Em(20)

    &.is-report
      grid-column: 1 / 4
      grid-row: 6 / 7
      margin-top: 3em

    &.is-article
      grid-column: 1 / 4
      grid-row: 7 / 8
      margin-top: 3em

  & &--action
    grid-column: 1 / 4
    grid-row: 4 / 5
    margin-top: 1em

  & &--footer
    &.is-publisher
      grid-column: 1 / 2
      grid-row: 3 / 4

    &.is-date
      grid-column: 3 / 4
      grid-row: 3 / 4

.l-unit__storyItem
  $root: &
  display: flex
  flex-direction: column
  position: relative
  align-items: center

  & &--header,
  & &--description,
  & &--action
    width: 100%

  & &--header
    margin-bottom: 1em

  & &--action
    margin-top: changeSizePx2Em(20)

  & &--footer
    position: absolute
    bottom: 1.2em
    left: 50%
    transform: translateX(-50%)
    opacity: 0
    +transition

  &[data-fetching="true"]
    #{$root}--footer
      opacity: 1

// object
.o-heading__page
  $root: &
  margin-bottom: 1em

  & &--item
    font-size: $font-xx-huge
    font-weight: bold
</style>
